import React, { useState, useEffect } from "react";
import { VictoryPie } from "victory";
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from "date-fns";

const MonthlyTasksChart = ({ staffId }) => {
  const [taskData, setTaskData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchTaskStats();
  }, [currentMonth]);

  
  const fetchTaskStats = async () => {
    const firstDay = format(startOfMonth(currentMonth), "yyyy-MM-dd");
    const lastDay = format(endOfMonth(currentMonth), "yyyy-MM-dd");

    try {
      const response = await fetch("https://api.insightos.co.uk/monthlytasks", {
        method: "POST",
        headers: { "Content-Type": "application/json",
         },
        credentials: "include",
        body: JSON.stringify({ firstDay, lastDay, id: staffId }),

      });

      const data = await response.json();
      if (data.success) {
        // Exclude "Break" and update state
        const filteredTasks = data.data.filter(task => task.task !== "Break");
        setTaskData(filteredTasks);
      }
    } catch (error) {
      console.error("Error fetching task data:", error);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-3 sm:p-4 lg:p-6 bg-white shadow-lg rounded-xl min-h-screen sm:min-h-0">
      {/* Header Section */}
      <div className="mb-6">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 mb-4 flex items-center">
          <span className="text-2xl mr-2">📊</span>
          Monthly Task Statistics
        </h1>
        
        {/* Month Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            data-component="monthly-task-stats"
            data-feature="load_previous_month_post"
            data-time-saved="2"
            className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-3 rounded-lg hover:from-blue-600 hover:to-indigo-700 active:scale-95 transition-all duration-200 font-medium shadow-md touch-manipulation flex items-center justify-center"
          >
            <span className="mr-2">←</span>
            <span className="text-sm sm:text-base">Previous</span>
          </button>
          
          <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800 text-center px-2">
            {format(currentMonth, "MMMM yyyy")}
          </h2>
          
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            data-component="monthly-task-stats"
            data-feature="load_next_month_post"
            data-time-saved="2"
            className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-3 rounded-lg hover:from-blue-600 hover:to-indigo-700 active:scale-95 transition-all duration-200 font-medium shadow-md touch-manipulation flex items-center justify-center"
          >
            <span className="text-sm sm:text-base">Next</span>
            <span className="ml-2">→</span>
          </button>
        </div>
      </div>

      {/* Task Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 sm:grid-cols-1 gap-4 sm:gap-6">
        {taskData.length > 0 ? (
          taskData.map((task) => (
            <div key={task.task} className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200">
              {/* Task Header */}
              <div className="mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2 leading-tight">
                  📋 {task.task}
                </h3>
                
                {/* Stats Summary */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold border border-green-200">
                    ✅ {task.completed} Complete
                  </span>
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold border border-red-200">
                    ❌ {task.notCompleted} Incomplete
                  </span>
                </div>
              </div>
              
              {/* Chart Container */}
              <div className="flex justify-center mb-4">
                <div className="w-full max-w-[280px] h-[200px] sm:h-[220px]">
                  <VictoryPie
                    data={[
                      { x: "Completed", y: task.completed || 0 },
                      { x: "Not Completed", y: task.notCompleted || 0 },
                    ]}
                    colorScale={["#22c55e", "#ef4444"]}
                    labels={({ datum }) => datum.y > 0 ? `${datum.y}` : ''}
                    labelRadius={({ innerRadius }) => innerRadius + 30}
                    innerRadius={40}
                    padAngle={2}
                    animate={{
                      duration: 1000,
                      onLoad: { duration: 500 }
                    }}
                    style={{ 
                      labels: { 
                        fontSize: 12, 
                        fontWeight: "bold",
                        fill: "white",
                        textShadow: "0 1px 2px rgba(0,0,0,0.5)"
                      }
                    }}
                  />
                </div>
              </div>
              
              {/* Issues Section */}
              <div className="border-t border-gray-200 pt-3">
                <div className="flex items-start gap-2">
                  <span className="text-xs font-semibold text-gray-700 flex-shrink-0 mt-0.5">
                    ⚠️ Issues:
                  </span>
                  <div className="flex-1">
                    {task.issues && task.issues > 0 ? (
                      <div className="bg-orange-50 border-l-4 border-orange-400 rounded-md p-2">
                        <span className="text-xs text-orange-800 font-medium">
                          {task.issues} issue{task.issues !== 1 ? 's' : ''} reported
                        </span>
                      </div>
                    ) : (
                      <div className="bg-green-50 border-l-4 border-green-400 rounded-md p-2">
                        <span className="text-xs text-green-700 font-medium">
                          No issues reported
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full">
            <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
              <div className="flex flex-col items-center space-y-3">
                <span className="text-6xl">📭</span>
                <span className="text-gray-500 text-lg font-medium">No task data available</span>
                <span className="text-gray-400 text-sm">
                  No tasks found for {format(currentMonth, "MMMM yyyy")}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyTasksChart;
